import {
  type HirerPaymentResult,
  type PaymentMethodType,
  PaymentsForCaja,
} from '@seek/payments-widget';
import { Stack } from 'braid-design-system';
import type React from 'react';
import { shallowEqual, useSelector } from 'react-redux';

import { SITE } from 'src/app/shared/constants/configObjectProperties';
import { SITE_DEFAULT } from 'src/app/shared/constants/site';
import {
  FEATURE_PER_UNIT_BILLING,
  useFeatureToggleData,
} from 'src/app/shared/featureToggle';
import { getConfig } from 'src/app/utils/config';
import { getEnvironment } from 'src/app/utils/environment';
import { isInvoiceOnly } from 'src/app/utils/payment/isInvoiceOnly';
import { getPaymentsTrackingAdapter } from 'src/app/v1/dataAnalytics';
import { useGenerateJobIdempotencyKey } from 'src/app/v1/views/hooks/useGenerateJobIdempotencyKey';
import { usePaymentsWidgetNation } from 'src/app/v1/views/hooks/usePaymentsWidgetNation';
import { PreviewButton } from 'src/app/v1/views/shared/PreviewButton';
import type { AppState } from 'src/types/appState';

interface PaymentsProps {
  handlePayAndPostButtonClick: ({
    paymentMethod,
    isSavedCreditCard,
  }: {
    paymentMethod: PaymentMethodType;
    isSavedCreditCard?: boolean;
  }) => Promise<void>;
  handlePaymentOptionChange: (
    _selectedIndex: number,
    selectedItem?: string,
  ) => void;
  isPaymentDetailsHidden: boolean;
  onPostAdButtonClickFailed: (error: Error, isHandled?: boolean) => void;
  onPaymentError: (error: Error) => void;
  onPaymentSuccess: (result: HirerPaymentResult) => void;
  onSaveCardOptionChanged: (shouldSave: boolean) => void;
  onSavedCardLoaded: () => void;
  onReplaceSavedCard: () => void;
  handledCancelPayment: () => void;
}
export const Payments: React.FC<PaymentsProps> = ({
  handlePaymentOptionChange,
  handlePayAndPostButtonClick,
  isPaymentDetailsHidden,
  onPaymentSuccess,
  onPaymentError,
  onPostAdButtonClickFailed,
  handledCancelPayment,
  onReplaceSavedCard,
  onSaveCardOptionChanged,
  onSavedCardLoaded,
}) => {
  const {
    adBudgetId,
    billToAgent,
    cart,
    contractBalance,
    currency,
    interactionId,
    jobOrderId,
    paymentOption,
  } = useSelector(
    (state: AppState) => ({
      adBudgetId: state.session.adBudget?.id,
      billToAgent: state.session.billToAgent,
      billingStatus: state.session.hirerAccount?.billingStatus,
      cart: state.session.cart,
      contractBalance: state.session.contractBalance,
      currency: state.session.productCurrency,
      interactionId: state.session.interactionId,
      jobOrderId: state.session.jobOrderId,

      paymentOption: state.formData.paymentOption,
    }),
    shallowEqual,
  );

  const paymentWidgetNation = usePaymentsWidgetNation();

  const orderId = cart.orderId ?? jobOrderId ?? '';
  const generatedIdempotencyKey = useGenerateJobIdempotencyKey();
  const invoiceOnly = isInvoiceOnly(contractBalance);

  const isPerUnitBilling = useFeatureToggleData(FEATURE_PER_UNIT_BILLING);

  const hirerCurrency = !isPerUnitBilling ? currency : 'AUD';

  if (!orderId || !hirerCurrency) {
    return null;
  }

  return (
    <Stack
      space="none"
      data={{ testid: 'apac-payments-widget', intercomid: 'paymentsSection' }}
    >
      <PaymentsForCaja
        advertiserNation={paymentWidgetNation}
        billToAgent={billToAgent}
        hideAllPayments={isPaymentDetailsHidden}
        invoiceOnly={isPerUnitBilling || invoiceOnly}
        currency={hirerCurrency}
        idempotencyKey={generatedIdempotencyKey}
        interactionId={interactionId}
        isAdBudgetSelected={Boolean(adBudgetId)}
        env={getEnvironment()}
        onPostAdButtonClicked={handlePayAndPostButtonClick}
        onPostAdButtonClickFailed={onPostAdButtonClickFailed}
        handledCancelPayment={handledCancelPayment}
        onPaymentMethodChanged={handlePaymentOptionChange}
        onSuccess={onPaymentSuccess}
        onError={onPaymentError}
        onReplaceSavedCard={onReplaceSavedCard}
        onSaveCardOptionChanged={onSaveCardOptionChanged}
        onSavedCardLoaded={onSavedCardLoaded}
        orderId={orderId}
        paymentMethod={paymentOption as PaymentMethodType}
        previewButtonComponent={<PreviewButton />}
        site={getConfig(SITE, SITE_DEFAULT)}
        trackingAdapter={getPaymentsTrackingAdapter()}
        useCase="CAJA"
      />
    </Stack>
  );
};
